import { RefObject, useCallback, useEffect, useRef } from 'react';
import Hammer from '@egjs/hammerjs';

const MIN_SCALE = 1;
const MAX_SCALE = 10;
const SWIPE_NAVIGATION_THRESHOLD = 60;
const SWIPE_AXIS_DOMINANCE = 1.5;
const EDGE_TOLERANCE = 1;

const clamp = (value, min, max) => Math.min(Math.max(min, value), max);

function useHammerEvents(
  autoSize: boolean,
  isLoaded: number,
  tapEvent: () => void,
  swipeEvent: RefObject<(direction: 'left' | 'right') => void>,
  element: RefObject<HTMLImageElement>,
  imgContainer: RefObject<HTMLDivElement>,
) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const hammertime = useRef<any>(null);

  const imageWidth = useRef(0);
  const imageHeight = useRef(0);
  const containerWidth = useRef(0);
  const containerHeight = useRef(0);
  const displayImageX = useRef(0);
  const displayImageY = useRef(0);
  const displayImageScale = useRef(1);

  const displayDefaultWidth = useRef(0);
  const displayDefaultHeight = useRef(0);

  const rangeX = useRef(0);
  const rangeMaxX = useRef(0);
  const rangeMinX = useRef(0);

  const rangeY = useRef(0);
  const rangeMaxY = useRef(0);
  const rangeMinY = useRef(0);

  const displayImageCurrentX = useRef(0);
  const displayImageCurrentY = useRef(0);
  const displayImageCurrentScale = useRef(1);

  const swipeActive = useRef(false);
  const panStartX = useRef(0);
  const pinchStart = useRef({
    focalX: 0,
    focalY: 0,
    scale: MIN_SCALE,
    x: 0,
    y: 0,
  });

  const updateRange = useCallback(() => {
    rangeX.current = Math.max(
      0,
      Math.round(displayDefaultWidth.current * displayImageCurrentScale.current) - containerWidth.current,
    );
    rangeY.current = Math.max(
      0,
      Math.round(displayDefaultHeight.current * displayImageCurrentScale.current) - containerHeight.current,
    );

    rangeMaxX.current = Math.round(rangeX.current / 2);
    rangeMinX.current = 0 - rangeMaxX.current;

    rangeMaxY.current = Math.round(rangeY.current / 2);
    rangeMinY.current = 0 - rangeMaxY.current;
  }, []);

  const clampScale = useCallback((newScale) => clamp(newScale, MIN_SCALE, MAX_SCALE), []);

  const getFocalPoint = useCallback(
    (clientX, clientY) => {
      const containerRect = imgContainer.current?.getBoundingClientRect();

      if (!containerRect) {
        return { x: 0, y: 0 };
      }

      return {
        x: clientX - (containerRect.left + containerRect.width / 2),
        y: clientY - (containerRect.top + containerRect.height / 2),
      };
    },
    [imgContainer],
  );

  const updateZoomAtPoint = useCallback(
    (newScale, previousScale, previousX, previousY, startFocalPoint, currentFocalPoint = startFocalPoint) => {
      const scaleRatio = newScale / previousScale;

      displayImageCurrentScale.current = newScale;
      updateRange();

      displayImageCurrentX.current = clamp(
        currentFocalPoint.x - (startFocalPoint.x - previousX) * scaleRatio,
        rangeMinX.current,
        rangeMaxX.current,
      );
      displayImageCurrentY.current = clamp(
        currentFocalPoint.y - (startFocalPoint.y - previousY) * scaleRatio,
        rangeMinY.current,
        rangeMaxY.current,
      );
    },
    [updateRange],
  );

  const updateDisplayImage = useCallback(
    (x, y, scale) => {
      const transform = `translateX(${x}px) translateY(${y}px) scale(${scale},${scale})`;

      if (element.current) {
        element.current.style.transform = transform;
        element.current.style.webkitTransform = transform;
        element.current.style.visibility = 'visible';
      }
    },
    [element],
  );

  const resizeContainer = useCallback(() => {
    containerWidth.current = imgContainer.current?.offsetWidth || 0;
    containerHeight.current = imgContainer.current?.offsetHeight || 0;

    if (displayDefaultWidth.current !== 0 && displayDefaultHeight.current !== 0) {
      displayDefaultWidth.current = element.current?.offsetWidth || 0;
      displayDefaultHeight.current = element.current?.offsetHeight || 0;

      updateRange();

      displayImageCurrentX.current = clamp(displayImageX.current, rangeMinX.current, rangeMaxX.current);
      displayImageCurrentY.current = clamp(displayImageY.current, rangeMinY.current, rangeMaxY.current);

      updateDisplayImage(displayImageCurrentX.current, displayImageCurrentY.current, displayImageCurrentScale.current);

      displayImageX.current = displayImageCurrentX.current;
      displayImageY.current = displayImageCurrentY.current;
    }
  }, [element, imgContainer, updateDisplayImage, updateRange]);

  useEffect(() => {
    // SFW navigation can replace the image element while new signed URLs load.
    if (hammertime.current && hammertime.current.element !== element.current) {
      hammertime.current.destroy();
      hammertime.current = null;
    }

    if (element.current && imgContainer.current) {
      if (!hammertime.current) {
        // @ts-ignore
        hammertime.current = new Hammer(element.current, {
          cssProps: {
            // enables ios callout menu on touch and hold when wanting to save image
            touchCallout: 'default',
            userDrag: 'none',
            userSelect: 'none',
          },
        });

        hammertime.current.get('pinch').set({ enable: true });
        hammertime.current.get('pan').set({ direction: Hammer.DIRECTION_ALL });
        hammertime.current.get('swipe').set({
          direction: Hammer.DIRECTION_HORIZONTAL,
          threshold: SWIPE_NAVIGATION_THRESHOLD,
          velocity: 1,
        });
      }

      if (isLoaded) {
        imageWidth.current = element.current.width;
        imageHeight.current = element.current.height;
        displayDefaultWidth.current = element.current.offsetWidth;
        displayDefaultHeight.current = element.current.offsetHeight;

        rangeX.current = Math.max(0, displayDefaultWidth.current - imgContainer.current.offsetWidth);
        rangeY.current = Math.max(0, displayDefaultHeight.current - imgContainer.current.offsetHeight);

        displayImageX.current = displayDefaultWidth.current / 2;
        displayImageY.current = displayDefaultHeight.current / 2;
        displayImageScale.current = MIN_SCALE;
        displayImageCurrentScale.current = MIN_SCALE;

        resizeContainer();
      }
    }
  }, [autoSize, element, imgContainer, isLoaded, resizeContainer]);

  const resizeListener = useCallback(() => resizeContainer(), [resizeContainer]);
  const mouseListener = useCallback((e) => e.preventDefault(), []);
  const wheelListener = useCallback(
    (ev) => {
      // Two mouse wheel features depending on autosize:
      // 1. use mouse wheel for panning when auto size is off because large image is shown
      // 2. zoom in feature for when auto size is on because the image is sized down to fit the screen
      if (!autoSize) {
        const isNegativeX = Math.sign(ev.deltaX) === -1;
        const isNegativeY = Math.sign(ev.deltaY) === -1;

        const deltaX = clamp(Math.abs(ev.deltaX), 0, 100);
        const deltaY = clamp(Math.abs(ev.deltaY), 0, 100);

        displayImageCurrentX.current = clamp(
          displayImageX.current + (isNegativeX ? deltaX : -deltaX),
          rangeMinX.current,
          rangeMaxX.current,
        );
        displayImageCurrentY.current = clamp(
          displayImageY.current + (isNegativeY ? deltaY : -deltaY),
          rangeMinY.current,
          rangeMaxY.current,
        );
      } else {
        // fix for firefox, they don't have a wheelDelta for some reason on their event
        const delta = ev.wheelDelta || ev.deltaY * -5;
        const previousScale = displayImageScale.current;
        const focalPoint = getFocalPoint(ev.clientX, ev.clientY);

        updateZoomAtPoint(
          clampScale(previousScale + delta / 400),
          previousScale,
          displayImageX.current,
          displayImageY.current,
          focalPoint,
        );
      }

      updateDisplayImage(displayImageCurrentX.current, displayImageCurrentY.current, displayImageCurrentScale.current);

      displayImageScale.current = displayImageCurrentScale.current;
      displayImageX.current = displayImageCurrentX.current;
      displayImageY.current = displayImageCurrentY.current;
    },
    [autoSize, clampScale, getFocalPoint, updateDisplayImage, updateZoomAtPoint],
  );

  useEffect(() => {
    const container = imgContainer.current;
    window.addEventListener('resize', resizeListener);
    container?.addEventListener('mousedown', mouseListener);
    container?.addEventListener('wheel', wheelListener);

    return () => {
      window.removeEventListener('resize', resizeListener);
      container?.removeEventListener('mousedown', mouseListener);
      container?.removeEventListener('wheel', wheelListener);
    };
  }, [imgContainer, isLoaded, mouseListener, resizeListener, wheelListener]);

  useEffect(() => {
    const manager = hammertime.current;

    if (!element.current || !manager) {
      return undefined;
    }

    const handleTap = () => tapEvent();
    const handlePanStart = () => {
      panStartX.current = displayImageX.current;
    };
    const handlePan = (ev) => {
      if (ev.maxPointers > 1) {
        return;
      }

      displayImageCurrentX.current = clamp(displayImageX.current + ev.deltaX, rangeMinX.current, rangeMaxX.current);
      displayImageCurrentY.current = clamp(displayImageY.current + ev.deltaY, rangeMinY.current, rangeMaxY.current);

      updateDisplayImage(displayImageCurrentX.current, displayImageCurrentY.current, displayImageScale.current);
    };
    const handlePinch = (ev) => {
      const currentFocalPoint = getFocalPoint(ev.center.x, ev.center.y);

      if (ev.type === 'pinchstart') {
        pinchStart.current = {
          focalX: currentFocalPoint.x - ev.deltaX,
          focalY: currentFocalPoint.y - ev.deltaY,
          scale: displayImageScale.current,
          x: displayImageX.current,
          y: displayImageY.current,
        };
      }

      const start = pinchStart.current;
      updateZoomAtPoint(
        clampScale(ev.scale * start.scale),
        start.scale,
        start.x,
        start.y,
        { x: start.focalX, y: start.focalY },
        currentFocalPoint,
      );
      updateDisplayImage(displayImageCurrentX.current, displayImageCurrentY.current, displayImageCurrentScale.current);
    };
    const handleGestureEnd = () => {
      displayImageScale.current = displayImageCurrentScale.current;
      displayImageX.current = displayImageCurrentX.current;
      displayImageY.current = displayImageCurrentY.current;
    };
    const handleSwipe = (ev) => {
      const horizontalDistance = Math.abs(ev.deltaX);
      const verticalDistance = Math.abs(ev.deltaY);
      const hasHorizontalIntent =
        horizontalDistance >= SWIPE_NAVIGATION_THRESHOLD &&
        horizontalDistance > verticalDistance * SWIPE_AXIS_DOMINANCE;
      const isSinglePointerGesture = ev.maxPointers === 1;
      const isSwipeLeft = ev.direction === Hammer.DIRECTION_LEFT;
      const isSwipeRight = ev.direction === Hammer.DIRECTION_RIGHT;
      const startedAtLeftBoundary = panStartX.current <= rangeMinX.current + EDGE_TOLERANCE;
      const startedAtRightBoundary = panStartX.current >= rangeMaxX.current - EDGE_TOLERANCE;
      const isMovingOutward = (isSwipeLeft && startedAtLeftBoundary) || (isSwipeRight && startedAtRightBoundary);

      if (!swipeActive.current && hasHorizontalIntent && isSinglePointerGesture && isMovingOutward) {
        swipeActive.current = true;

        // set timeout to debounce swipe events
        setTimeout(() => {
          swipeActive.current = false;
        }, 500);

        // left arrow takes you back and right arrow takes you forward on the UI, which means...
        // invert the direction, swipe to the left to advance, swipe right to go backward
        swipeEvent.current?.(ev.direction === Hammer.DIRECTION_LEFT ? 'right' : 'left');
      }
    };

    manager.on('tap', handleTap);
    manager.on('panstart', handlePanStart);
    manager.on('pan', handlePan);
    manager.on('pinchstart pinchmove', handlePinch);
    manager.on('panend pancancel pinchend pinchcancel', handleGestureEnd);
    manager.on('swipe', handleSwipe);

    return () => {
      manager.off('tap', handleTap);
      manager.off('panstart', handlePanStart);
      manager.off('pan', handlePan);
      manager.off('pinchstart pinchmove', handlePinch);
      manager.off('panend pancancel pinchend pinchcancel', handleGestureEnd);
      manager.off('swipe', handleSwipe);
    };
  }, [clampScale, element, getFocalPoint, isLoaded, tapEvent, updateDisplayImage, updateZoomAtPoint, swipeEvent]);

  useEffect(
    () => () => {
      hammertime.current?.destroy();
      hammertime.current = null;
    },
    [],
  );
}

export default useHammerEvents;
